import { Currency as PackageCurrency, Frequency, Package, Pricing } from '@vendasta/marketplace-packages';
import { UILineItem } from '@vendasta/store';
import { LineItem, Revenue, RevenueComponent, RevenuePeriod } from '@vendasta/sales-orders';
import { UILineItemWithEditionPricing } from './interface';
import { defaultRevenue, defaultRevenuePeriodMonthly } from './constants';
import { App } from '@galaxy/marketplace-apps';
import { AppWithRetailPricing } from '../components/line-items/line-items.interface';

export function packageToUILineItem(pkg: Package, currency: string): UILineItem {
  return {
    lineItem: packageToSalesOrderLineItem(pkg, currency),
    name: pkg.name,
    iconUrl: pkg.icon,
    bannerUrl: pkg.headerImageUrl,
    isMultiActivatable: true,
    storeLineItem: packageToSalesOrderLineItem(pkg, currency),
    discountPercentage: 0,
    description: pkg.tagline,
  };
}

export function packageToSalesOrderLineItem(pkg: Package, currency: string): LineItem {
  return new LineItem({
    packageId: pkg.packageId,
    currencyCode: !!pkg.pricing && !!pkg.pricing.currency ? PackageCurrency[pkg.pricing.currency] : currency,
    currentRevenue: new Revenue({ revenueComponents: buildPackageRevenueComponentsList(pkg.pricing) }),
    quantity: 1,
  });
}

// Pricing input is specific to Package type
// The pricing prices list may contain only a single object with just the startingAt property set to true in the case of contact sales and free packages
export function buildPackageRevenueComponentsList(pricing: Pricing): RevenueComponent[] {
  const prices = !!pricing && !!pricing.prices ? pricing.prices : [];
  if (prices.length === 0) {
    return [defaultRevenue];
  }
  return prices.map((p) => {
    return new RevenueComponent({
      value: p.price && p.price > -1 ? p.price : 0,
      period: packageFrequencyToSalesOrderPeriod(p.frequency),
      isStartingRevenue: p.isStartingPrice,
    });
  });
}

export function packageFrequencyToSalesOrderPeriod(f: Frequency): RevenuePeriod {
  switch (f) {
    case Frequency.DAILY:
      return RevenuePeriod.DAILY;
    case Frequency.MONTHLY:
      return RevenuePeriod.MONTHLY;
    case Frequency.ONCE:
      return RevenuePeriod.ONETIME;
    case Frequency.WEEKLY:
      return RevenuePeriod.WEEKLY;
    case Frequency.YEARLY:
      return RevenuePeriod.YEARLY;
    default:
      return defaultRevenuePeriodMonthly;
  }
}

export function convertSelectedAppToLineItem(
  app: App | AppWithRetailPricing,
  defaultDisplayCurrency: string,
): UILineItem {
  const uiLineItem: UILineItemWithEditionPricing = {
    lineItem: appToSalesOrderLineItem(app, defaultDisplayCurrency),
    name: app.sharedMarketingInformation.name,
    editionName: app.sharedMarketingInformation.editionName,
    iconUrl: app.sharedMarketingInformation.iconUrl,
    bannerUrl: app.sharedMarketingInformation.bannerImageUrl,
    storeLineItem: appToSalesOrderLineItem(app, defaultDisplayCurrency),
    discountPercentage: 0,
    description: app.sharedMarketingInformation.tagline,
    appType: app.appType,
  };

  if (app.price) {
    uiLineItem.usesCustomPricing = app.price.usesCustomPricing;
  }

  if (app.activationInformation) {
    uiLineItem.isMultiActivatable = app.activationInformation.multipleActivationsEnabled;
  }

  if ('retailPricing' in app) {
    // override the default revenue component if retail pricing exists
    if (app.retailPricing.revenue.revenueComponents.length > 0) {
      uiLineItem.lineItem.currentRevenue = app.retailPricing.revenue;
      uiLineItem.lineItem.currencyCode = app.retailPricing.currencyCode;
      uiLineItem.storeLineItem.currentRevenue = app.retailPricing.revenue;
      uiLineItem.storeLineItem.currencyCode = app.retailPricing.currencyCode;
    }
  }

  if (app.parentRequirements?.enabled) {
    uiLineItem.parentIconUrl = app.parentRequirements.parentDetails.iconUrl;
    uiLineItem.parentName = app.parentRequirements.parentDetails.name;
    uiLineItem.parentAppId = app.parentRequirements.parentDetails.key.appId;
  }
  return uiLineItem;
}

export function appToSalesOrderLineItem(app: App, currency: string): LineItem {
  return new LineItem({
    appKey: app.key,
    currencyCode: currency,
    currentRevenue: new Revenue({ revenueComponents: [defaultRevenue] }),
    quantity: 1,
  });
}

export function calculateDiscountPercentage(uiLineItem: UILineItem): number {
  const storeRevenueComponents = uiLineItem.storeLineItem?.currentRevenue?.revenueComponents;
  if (storeRevenueComponents === null) {
    return 0;
  }

  const storeYearlyTotalEstimate = storeRevenueComponents.reduce((total, storeRevenueComponent) => {
    return total + getYearlyTotal(storeRevenueComponent);
  }, 0);
  if (storeYearlyTotalEstimate <= 0) {
    return 0;
  }

  const orderRevenueComponents = uiLineItem.lineItem?.currentRevenue?.revenueComponents;
  if (orderRevenueComponents === null) {
    return 0;
  }
  const orderYearlyTotalEstimate = orderRevenueComponents.reduce((total, orderRevenueComponent) => {
    return total + getYearlyTotal(orderRevenueComponent);
  }, 0);
  if (orderYearlyTotalEstimate <= 0) {
    return 0;
  }

  const discountPercentage = ((storeYearlyTotalEstimate - orderYearlyTotalEstimate) * 100) / storeYearlyTotalEstimate;
  if (discountPercentage > 100 || discountPercentage < 0) {
    return 0;
  }
  if (0 < discountPercentage && discountPercentage < 1) {
    return 1;
  }
  return Math.floor(discountPercentage);
}

function getYearlyTotal(revenueComponent: RevenueComponent): number {
  switch (revenueComponent.period) {
    case RevenuePeriod.YEARLY:
      return revenueComponent.value;
    case RevenuePeriod.MONTHLY:
      return revenueComponent.value * 12;
    case RevenuePeriod.BIWEEKLY:
      return revenueComponent.value * 26;
    case RevenuePeriod.WEEKLY:
      return revenueComponent.value * 52;
    case RevenuePeriod.DAILY:
      return revenueComponent.value * 365;
    default:
      return revenueComponent.value;
  }
}
